import "./src/styles/global.css";
import {
  captureUtmParams,
  initLinkedInInsightTag,
  loadGtm,
} from "./src/utils/tracking";
import { captureReferral } from "./src/utils/referral";
import { captureWhenReady, setPostHog, withPostHog } from "./src/utils/posthog";
import { addExperimentProps, watchExposures } from "./src/utils/experiments";
import { afterLoad, onIntentOrTimeout } from "./src/utils/deferred";

const GTM_ID = "GTM-TWMGTLWN";

// Marketing tags and the session recorder wait for the first scroll/tap/key,
// or this long after load.
const MARKETING_TAG_TIMEOUT_MS = 5000;

const initPostHog = (key) => {
  // Use your reverse proxy in production; direct PostHog in development
  const isProduction =
    window.location.origin === "https://reflektlab.ai" ||
    window.location.origin === "https://www.reflektlab.ai";
  const apiHost = isProduction
    ? "https://phr.reflektlab.ai"
    : process.env.GATSBY_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";
  // Imported here, not at the top of the file, so posthog-js (93 KB gzipped)
  // is no longer in the JS every page loads before it can hydrate.
  import("posthog-js").then(({ default: posthog }) => {
    posthog.init(key, {
      api_host: apiHost,
      ui_host: "https://eu.posthog.com",
      person_profiles: "identified_only",
      // onRouteUpdate below captures $pageview, and Gatsby calls it on the
      // initial route too. Leaving posthog-js to capture on init as well made
      // every session record two $pageview events for its landing page, about
      // 20ms apart, so $pageview_count was always one too high (RFLKT-7095).
      capture_pageview: false,
      // capture_pageleave defaults to "if_capture_pageview", so turning the
      // pageview off silently takes $pageleave with it. Keep it on explicitly.
      capture_pageleave: true,
      // Surveys and dead-click capture are off in the project; saying so here
      // keeps posthog-js from fetching their bundles if that ever drifts.
      // Conversations (the support chat) and web vitals stay on.
      disable_surveys: true,
      capture_dead_clicks: false,
      // The recorder (67 KB, an 88 ms long task on PSI desktop) starts on the
      // visitor's first interaction or 5 s after load, like the marketing
      // tags, rather than right after init (~1.1 s), where it landed inside
      // Lighthouse's TBT window. startSessionRecording() without arguments
      // still honours the project's sample rate (50% since 2026-10-23).
      disable_session_recording: true,
      // $feature/<key> = arm on every event of a page in an experiment picked
      // at the edge (#529). A no-op on every other page.
      before_send: addExperimentProps,
    });
    setPostHog(posthog);
    // Exposures of an experiment picked at the edge, once per page load.
    if (posthog.__loaded) watchExposures(posthog, true);
    onIntentOrTimeout(
      () => posthog.startSessionRecording(),
      MARKETING_TAG_TIMEOUT_MS,
    );
  });
};

export const onClientEntry = () => {
  const key = process.env.GATSBY_PUBLIC_POSTHOG_KEY;
  if (typeof window !== "undefined" && key) {
    afterLoad(() => initPostHog(key));
  } else if (typeof window !== "undefined" && !key) {
    console.warn(
      "[PostHog] GATSBY_PUBLIC_POSTHOG_KEY is missing. Add it to .env.local and restart gatsby develop.",
    );
  }

  // Capture UTM params from the landing URL (first-touch attribution). Runs
  // now, while the landing URL is still in the address bar; the PostHog super
  // properties it sets are queued until PostHog loads.
  captureUtmParams();

  // Partner code from ?ref=, kept 90 days, last touch wins (#344).
  captureReferral();

  // GTM (GA4 inside it) and LinkedIn Insight, off the critical path. GTM was
  // a <Script strategy="idle"> in Layout.tsx and StartLandingPage.tsx; it now
  // loads once for the whole app, production only as before.
  const linkedInPartnerId =
    process.env.GATSBY_PUBLIC_LINKEDIN_PARTNER_ID || "8728234";
  onIntentOrTimeout(() => {
    if (process.env.NODE_ENV === "production") loadGtm(GTM_ID);
    initLinkedInInsightTag(linkedInPartnerId);
  }, MARKETING_TAG_TIMEOUT_MS);
};

let isFirstRoute = true;

export const onRouteUpdate = () => {
  if (typeof window === "undefined") return;
  // Queued until PostHog loads; the URL is read now, so a visitor who moves on
  // before then still gets this page's view, not the next one's.
  // The tested block of an experiment is re-rendered on each page, so a
  // pending in-view exposure watches the new one. No-op until PostHog is up
  // (the first watch starts right after init) and on pages in no experiment.
  if (!isFirstRoute) withPostHog((ph) => watchExposures(ph, false));
  isFirstRoute = false;
  captureWhenReady("$pageview", {
    $current_url: window.location.href,
    $pathname: window.location.pathname,
  });
  // GA4 SPA pageviews. Nothing in the GTM container listens for this event
  // (GA4's enhanced measurement picks up history changes itself), and before
  // GTM has loaded there is no dataLayer, so this is a no-op until then.
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: "gatsby-route-change" });
  }
};
